import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useDeleteApiCtpsByIdMutation,
  useGetApiCtpsByIdQuery,
  useGetApiUsersMeQuery,
  usePostApiCtpsByIdSubmitMutation,
} from '../../app/athenaApiGen';
import DynamicForm from '../../components/dynamic-form/DynamicForm';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import {
  createFormDataChangeHandler,
  parseRawFormData,
  toNumber,
} from '../../dashboard/components/formUtils';
import useSchemaForm from '../../hooks/useSchemaForm';
import { ctpFormSchema } from './CTPFormSchema';

const DEBUG_MODE = false; // Toggle this for debug features

export default function CTPForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: meData } = useGetApiUsersMeQuery();
  const [formData, setFormData, isLoading, resetFormData] =
    useSchemaForm('CTPFormSchema');
  const [initialData, setInitialData] = React.useState(null);
  const { data: ctpResp, isFetching: isFetchingCTP } = useGetApiCtpsByIdQuery(
    { id: id || '' },
    { skip: !id },
  );
  const [deleteCtp, { isLoading: deleting }] = useDeleteApiCtpsByIdMutation();
  const [submitCtp] = usePostApiCtpsByIdSubmitMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isCloneMode = searchParams.get('mode') === 'clone';
  const [savedCtpId, setSavedCtpId] = React.useState(isCloneMode ? null : (id || null)); // Store ID after save

  // Always start fresh on no-id route navigations like /ctp
  useEffect(() => {
    if (id) return;
    resetFormData();
    setSavedCtpId(null);
    setInitialData(null);
    setDeleteDialogOpen(false);
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, [id, location.key, resetFormData]);

  const parsedRaw = useMemo(() => {
    const raw = ctpResp?.rawFormData || ctpResp?.RawFormData;
    return raw ? parseRawFormData(raw) : null;
  }, [ctpResp]);

  // Compute balance calculations reactively
  useEffect(() => {
    const clinValue = toNumber(formData?.clinx002Value);
    const cumulative = toNumber(formData?.cumulativeAmountBilled);
    const currentBal = clinValue - cumulative;
    const newBal = currentBal; // For CTP, new balance is same as current since it's for purchase

    setFormData((prev) => {
      const next = { ...prev };
      const nextCurrentBal = String(currentBal.toFixed(2));
      const nextNewBal = String(newBal.toFixed(2));
      if (next.currentCLINX002Balance !== nextCurrentBal)
        next.currentCLINX002Balance = nextCurrentBal;
      if (next.newCLINX002Balance !== nextNewBal)
        next.newCLINX002Balance = nextNewBal;
      return next;
    });
  }, [formData?.clinx002Value, formData?.cumulativeAmountBilled, setFormData]);

  useEffect(() => {
    if (!id) return;
    if (!parsedRaw && ctpResp) {
      const normalized = { ...ctpResp };
      // Convert date fields to dayjs objects regardless of source
      if (normalized.ctpDate) normalized.ctpDate = dayjs(normalized.ctpDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      normalized.workflowState = isCloneMode ? '' : (ctpResp.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
      // Store initial data for attachment change tracking (only set once)
      if (!initialData) {
        setInitialData(normalized);
      }
      return;
    }
    if (parsedRaw) {
      const normalized = { ...parsedRaw };
      if (normalized.ctpDate) normalized.ctpDate = dayjs(normalized.ctpDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      normalized.workflowState = isCloneMode ? '' : (ctpResp?.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
      // Store initial data for attachment change tracking (only set once)
      if (!initialData) {
        setInitialData(normalized);
      }
    }
  }, [id, isCloneMode, parsedRaw, ctpResp, setFormData, initialData]);

  if (isLoading || (id && isFetchingCTP && !parsedRaw)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleFormDataChange = createFormDataChangeHandler(setFormData);

  // Default to view mode when editing an existing record (id present),
  // unless explicitly set to edit via ?mode=edit.
  // Also force view mode if workflow has progressed past Pending.
  const editableWorkflow = !formData?.workflowState || formData.workflowState === 'Pending';
  const isViewMode = isCloneMode ? false : (id ? searchParams.get('mode') !== 'edit' || !editableWorkflow : false);

  // Handle cancel — act like the browser back button so callers from the
  // approval queue or elsewhere land back on the page they came from
  // instead of being forced to the CTP list.
  const handleCancel = () => {
    navigate(-1);
  };

  // Handle successful submission - navigate back to list
  const handleSubmitSuccess = (result) => {
    if (result.ok && result.payload) {
      // Store the returned ID for subsequent saves
      if (result.payload.ctpId) {
        setSavedCtpId(result.payload.ctpId);
      }
    }
    if (result.ok && !result.skipNavigation) {
      setTimeout(() => navigate('/ctp-list'), 2500);
    }
  };

  // Handle delete
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCtp({ id: id }).unwrap();
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'CTP deleted successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/ctp-list'), 2500);
    } catch (error) {
      console.error('Failed to delete CTP', error);
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Failed to delete CTP. Please try again.',
        severity: 'error',
      });
    }
  };

  const canDelete = id && editableWorkflow && formData?.workflowState !== undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '100%' } }}>
      <DynamicForm
        schema={ctpFormSchema}
        formData={formData}
        setFormData={handleFormDataChange}
        onSubmit={handleSubmitSuccess}
        showSubmit={isCloneMode || !savedCtpId}
        isEditMode={!isCloneMode && Boolean(savedCtpId)}
        recordId={isCloneMode ? null : savedCtpId}
        readOnly={isViewMode}
        onCancel={handleCancel}
        onDelete={canDelete ? handleDeleteClick : undefined}
        submitMutation={submitCtp}
        initialData={initialData}
        authorId={meData?.contact?.contactId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={deleteDialogOpen}
        title="Delete CTP"
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `
            Are you sure you want to delete CTP <strong>${formData?.ctpNumber}</strong>?
            <br /><br />
            This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
